<template>
  <div>
    <vx-card class="mt-base mb-5" title="Learning & Resources">
      <h4 class="mb-5">1. What Is Forensic Science?</h4>
      <p class="mb-5">
        Forensic science can be a complicated business, that’s for sure - though if you’re
        considering it as a career, it’s likely you already have some idea of what it involves.
        Especially if you’re a fan of crime shows on Netflix (Making A Murderer, anyone?).
      </p>
      <p class="mb-5">
        To get a little more insight into what the world of forensics looks like, check out this
        video and then complete the questions that follow.
      </p>
      <responsive-youtube-wrapper :image="videos[1].image" :video="videos[1].video" />

      <div v-for="(question, index) in questions1" :key="'q1_' + index">
        <h5 class="mb-5 questionlabel" v-html="index + '. ' + question.question"></h5>
        <radio-buttons-wrapper
          v-model="data['question1']"
          :index="'q1_' + index"
          :question="question"
          :is-editable="isEditable"
          show-question="false"
        />
      </div>
      <h5 class="mb-5">2. Read through the definitions below.</h5>
      <table class="table default-table">
        <tr class="subheader">
          <th scope="col" colspan="2">Definitions</th>
        </tr>
        <tr class="body" v-for="(definition, def_key) in definitions" :key="'def_' + def_key">
          <th scope="row" class="subheader" width="10%">{{ def_key }}</th>
          <td>{{ definition }}</td>
        </tr>
      </table>
      <p class="mb-5">
        Match each term below with the correct definition from the table above by writing the
        corresponding letter in the right hand column.
      </p>

      <table class="table default-table">
        <tr class="subheader">
          <th scope="col" colspan="2" width="60%">Term</th>
          <th scope="col" width="40%">Definition</th>
        </tr>
        <tr class="body" v-for="(answer, def_key) in definitions_answers" :key="'def_' + def_key">
          <th scope="row" class="subheader" width="5%">{{ def_key }}</th>
          <td>{{ answer.answer }}</td>
          <td>
            <div
              class="vx-col w-full mb-2"
              v-if="isEditable"
              :class="{
                wrong:
                  data['question' + (2 + def_key)] > 0 &&
                  answer.correct != data['question' + (2 + def_key)],
              }"
            >
              <select
                class="default-select"
                v-model="data['question' + (2 + def_key)]"
                :name="'question_' + (2 + def_key)"
                :aria-label="`Definition for ${answer.answer}`"
              >
                <option value="undefined">Please Select</option>
                <option
                  v-for="(answer, answer_key) in question_2_answers"
                  :key="'q2a_' + answer_key"
                  :value="answer_key"
                >
                  {{ answer }}
                </option>
              </select>
            </div>
            <div class="my-2 mx-5" v-else>
              <div
                v-if="
                  data['question' + (2 + def_key)] != 0 && data['question' + (2 + def_key)] != null
                "
              >
                <p
                  class="my-2"
                  v-for="(answer, answer_key) in question_2_answers"
                  :key="'q2a_' + answer_key"
                >
                  <span v-if="data['question' + (2 + def_key)] == answer">{{ answer }}</span>
                </p>
              </div>
              <p class="my-2" v-else>N/A</p>
            </div>
          </td>
        </tr>
      </table>
      <button class="btn btn-primary btn-size-medium mr-3" @click="save()" :disabled="disableSave">
        Save
      </button>
      <vs-progress indeterminate color="primary" v-if="showSaveProgress"></vs-progress>
    </vx-card>
    <vx-card>
      <h4 class="mb-5">2. Forensic Science - Have You Got What It Takes?</h4>

      <p class="mb-5">
        How much do you know about what it takes to be a forensic scientist? Test yourself in this
        quiz.
      </p>
      <p class="mb-5">
        Test your current knowledge by answering the 20 questions in the quiz below (click the image
        below to access the quiz).
      </p>
      <p class="mb-5">
        *To find out if your knowledge has improved, you can retake the quiz after you’ve finished
        your virtual work experience!
      </p>

      <centered-image-wrapper
        classes="2/3"
        :image="image1"
        alt="Take the forensic science quiz on Grizly (opens in a new tab)"
        link="https://grizly.com/quizzes/forensic-science-quiz-do-you-have-the-guts-to-kill-it-in-forensics"
      />
      <p class="mb-5">Record your results below.</p>

      <div class="overflow-hidden">
        <p class="float-left py-2 pr-2"><strong>What score out of 20 did you get?</strong></p>
        <text-block
          v-model="data['question5']"
          :editable="isEditable"
          label="What score out of 20 did you get?"
          style="width: 100px; float: left"
          pclass="my-2"
        />
        <p class="float-left py-2 pl-2">/ 20</p>
      </div>
      <div class="overflow-hidden">
        <p class="float-left py-2 pr-2"><strong>What grade did you get?</strong></p>
        <text-block
          v-model="data['question6']"
          :editable="isEditable"
          label="What grade did you get?"
          style="width: 100px; float: left"
          pclass="my-2"
        />
      </div>
      <button class="btn btn-primary btn-size-medium mr-3" @click="save()" :disabled="disableSave">
        Save
      </button>
      <vs-progress indeterminate color="primary" v-if="showSaveProgress"></vs-progress>
    </vx-card>
    <vx-card>
      <h4 class="mb-5">3. Working as a Forensic Scientist</h4>

      <p class="mb-5">
        There are lots of different areas to specialise in within the field of forensic science.
      </p>
      <p class="mb-5">
        Watch the video below to get an insight into the working life of a forensic scientist who
        specialises in trace evidence in the United States.
      </p>
      <responsive-youtube-wrapper :image="videos[2].image" :video="videos[2].video" />

      <p class="mb-5">
        From what you know about the work of forensic scientists already, which part of the job
        appeals to you the most?
      </p>
      <textarea-block
        v-model="data.question7"
        :editable="isEditable"
        label="From what you know about the work of forensic scientists already, which part of the job appeals to you the most?"
      />

      <p class="mb-5">Which part of the job appeals to you the least?</p>
      <textarea-block
        v-model="data.question8"
        :editable="isEditable"
        label="Which part of the job appeals to you the least?"
      />
      <button class="btn btn-primary btn-size-medium mr-3" @click="save()" :disabled="disableSave">
        Save
      </button>
      <vs-progress indeterminate color="primary" v-if="showSaveProgress"></vs-progress>
    </vx-card>
    <vx-card>
      <h4 class="mb-5">4. Crime Scene Processing</h4>
      <p class="mb-5">
        For a forensic scientist, one of the most important duties is testing evidence gathered at
        crime scenes - that can mean either working in the lab, or at the crime scene itself.
      </p>
      <p class="mb-5">
        Crime scene investigation is important in establishing what happened and who was
        responsible. There is a lot riding on this type of work, so it’s important that clear
        procedures are developed to prevent mistakes from being made. After all, forensic evidence
        is frequently used to send people to jail - so you’ve got to be sure!
      </p>
      <p class="mb-5">
        Different organisations process crime scenes in different ways, the video below outlines a
        method called the “Seven S Method”.
      </p>
      <responsive-youtube-wrapper :image="videos[3].image" :video="videos[3].video" />
    </vx-card>
    <vx-card>
      <h4 class="mb-5">5. Types of Forensic Evidence</h4>
      <p class="mb-5">
        There are many different types of evidence that forensic scientists work with. In the video
        below, the presenter examines many of these different evidence types and how they can be
        classified.
      </p>
      <p class="mb-5">
        There are some suggested tasks you can do as you work your way through the video. Some of
        them ask you to work with a partner; however, they can easily be adapted for doing by
        yourself too.
      </p>
      <responsive-youtube-wrapper :image="videos[4].image" :video="videos[4].video" />
      <p class="mb-5">
        Next, in the <i>Activities</i> section, you’ll learn more about the different types of
        evidence. You’ll also get the chance to examine some evidence too - cool, right?
      </p>
    </vx-card>
  </div>
</template>

<script>
import RadioButtonsWrapper from '@/views/WorkExperiences/components/ActivitiesBlocks/RadioButtonsWrapper';
import methods from '@/views/WorkExperiences/components/lr_js/methods';
import computed from '@/views/WorkExperiences/components/lr_js/computed';
import inject from '@/views/WorkExperiences/components/lr_js/inject';
import store from '@/store/store';

export default {
  components: {
    RadioButtonsWrapper,
  },
  data() {
    return {
      disableSave: false,
      showSaveProgress: false,
      videos: {
        1: {
          image: require('@/assets/images/experiences/forensic_science/l_r_vid_1_splash.jpg'),
          video: 'https://www.youtube.com/embed/h3-Pj-zbEq8',
        },
        2: {
          image: require('@/assets/images/experiences/forensic_science/l_r_vid_2_splash.jpg'),
          video: 'https://www.youtube.com/embed/hVL28IaCUw4',
        },
        3: {
          image: require('@/assets/images/experiences/forensic_science/l_r_vid_3_splash.jpg'),
          video: 'https://www.youtube.com/embed/6sKptN1GGFc',
        },
        4: {
          image: require('@/assets/images/experiences/forensic_science/l_r_vid_4_splash.jpg'),
          video: 'https://www.youtube.com/embed/ETo8-yi6Ud4',
        },
      },
      definitions: {
        'a.': 'The stiffening of the joints and muscles of a body a few hours after death, usually lasting from one to four days.',
        'b.': 'The gravitational pooling of blood to lower areas of the body resulting in a red/purple colouration.',
        'c.': 'the gradual cooling of the body following death',
      },
      definitions_answers: {
        1: { answer: 'Livor Mortis', correct: 2 },
        2: { answer: 'Rigor Mortis', correct: 1 },
        3: { answer: 'Algor Mortis', correct: 3 },
      },
      question_2_answers: {
        1: 'a',
        2: 'b',
        3: 'c',
      },
      questions1: {
        1: {
          question: `What does the Latin word <span style="font-style: italic;">mortis</span> mean?`,
          answers: [
            { val: 1, answer: 'Old', correct: false },
            { val: 2, answer: 'Death', correct: true },
            { val: 3, answer: 'Lock', correct: false },
          ],
        },
      },
      image1: require('@/assets/images/experiences/forensic_science/quiz_button.jpg'),
      showVideoPopupYt: false,
      ytVideoUrl: false,
    };
  },
  methods: methods,
  computed: {
    ...computed,
    activeExperienceID() {
      return store.getters['authStudent/activeExperienceID'];
    },
    experience() {
      const loginType = localStorage.getItem('login_type');
      if (loginType == 'student') {
        const experiences = store.getters['authStudent/experiences'];
        return experiences.find((i) => i.id == this.activeExperienceID);
      } else {
        return store.getters['authTeacher/experience'];
      }
    },
  },
  inject: inject,
  beforeMount() {
    store.commit('authStudent/setActiveExperienceID', this.$route.params.id);
  },
};
</script>
<style scoped>
.wrong {
  background: rgba(202, 20, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
  padding: 0 5px;
  margin: 0;
}
</style>
